import React, { useState, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertCircle, Upload, Camera, Save, SkipForward, CheckCircle2, Trash2, Filter, Users, Building2 } from "lucide-react";

interface CuyahogaCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  duplicates: number;
  familyTransfers: number;
  failed: number;
  failedRecords: Array<{ docNumber: string; error: string }>;
}

interface FilterStats {
  total: number;
  landContracts: number;
  familyTransfers: number;
  passed: number;
}

interface ExtractedRecord {
  document_number: string | null;
  document_type: string | null;
  filing_date: string | null;
  grantor_name: string | null;
  grantor_address: string | null;
  grantee_name: string | null;
  grantee_address: string | null;
  property_address: string | null;
  legal_description: string | null;
  loan_amount: number | null;
  interest_rate: number | null;
  monthly_payment: number | null;
  term_months: number | null;
  balloon_amount: number | null;
  balloon_due_date: string | null;
}

export const CuyahogaCountyTextParser = ({ onRecordsSaved }: CuyahogaCountyTextParserProps) => {
  // Import mode state
  const [pastedText, setPastedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [filterStats, setFilterStats] = useState<FilterStats | null>(null);

  // Screenshot capture state
  const [imageData, setImageData] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedRecord | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);

  // Check if a name looks like a business entity
  const isBusinessEntity = (name: string): boolean => {
    if (!name) return false;
    const upper = name.toUpperCase();
    return /\b(LLC|INC|CORP|LTD|TRUST|BANK|PROPERTIES|HOLDINGS|INVESTMENTS|REALTY|MANAGEMENT|ENTERPRISE|ASSOCIATION|GROUP|PARTNERS)\b/.test(upper);
  };

  // Check if a name is a cancellation marker
  const isCancellation = (name: string): boolean => {
    if (!name) return false;
    const upper = name.toUpperCase().trim();
    return upper === 'CANCELLATION' || upper === 'CANELLATION' || upper.startsWith('CANCELLATION') || upper.startsWith('RELEASE FOR') || upper.startsWith('CANCEL');
  };

  // Filter detection functions
  const isFamilyTransfer = (grantor: string, grantee: string): boolean => {
    if (!grantor || !grantee) return false;

    // Skip filter if either side is a cancellation
    if (isCancellation(grantor) || isCancellation(grantee)) return false;

    // Skip filter if names are identical (self-reference, not a transfer)
    const grantorNorm = grantor.toUpperCase().replace(/[^A-Z ]/g, '').trim();
    const granteeNorm = grantee.toUpperCase().replace(/[^A-Z ]/g, '').trim();
    if (grantorNorm === granteeNorm) return false;

    // Skip filter if either party is a business entity
    if (isBusinessEntity(grantor) || isBusinessEntity(grantee)) return false;
    
    const extractLastName = (name: string): string => {
      const cleaned = name.toUpperCase().replace(/[,&]/g, ' ').trim();
      const parts = cleaned.split(/\s+/).filter(p => p.length > 1);
      if (parts.length === 0) return '';
      return parts[0];
    };

    const grantorLastName = extractLastName(grantor);
    const granteeLastName = extractLastName(grantee);

    if (!grantorLastName || !granteeLastName) return false;
    
    return grantorLastName === granteeLastName;
  };

  const parseRecords = (text: string) => {
    const rawLines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const records = [];
    const stats: FilterStats = {
      total: 0,
      landContracts: 0,
      familyTransfers: 0,
      passed: 0
    };

    // Pre-process: merge multi-line records from web copy-paste
    // The county website produces records that span 2-3 lines with "menu icon" prefixes
    // Merge lines: a line containing "LAND CONTRACT" starts a new record, 
    // subsequent lines without it are continuation data
    const mergedLines: string[] = [];
    
    for (const line of rawLines) {
      // Skip pure "menu icon" or empty decorative lines
      if (/^menu\s*icon$/i.test(line)) continue;
      // Strip leading "menu icon" text
      const cleaned = line.replace(/^menu\s*icon\s*/i, '').trim();
      if (!cleaned) continue;
      
      const upper = cleaned.toUpperCase();
      if (upper.includes('LAND CONTRACT')) {
        mergedLines.push(cleaned);
      } else if (mergedLines.length > 0) {
        // Append to previous line as continuation
        mergedLines[mergedLines.length - 1] += '\t' + cleaned;
      }
    }

    for (const line of mergedLines) {
      // Skip header lines
      if (line.toLowerCase().includes('document') && line.toLowerCase().includes('type')) continue;
      if (line.toLowerCase().includes('grantor') && line.toLowerCase().includes('grantee')) continue;
      
      // Try tab-separated first
      let columns = line.split('\t');

      // For tab-separated data: auto-detect column containing "LAND CONTRACT"
      if (columns.length >= 4) {
        const lcColIdx = columns.findIndex(c => c.trim().toUpperCase().includes('LAND CONTRACT'));
        
        if (lcColIdx >= 0) {
          // Re-map columns based on detected LAND CONTRACT position
          // Expected web format: [grantor] [grantee] [LAND CONTRACT] [date] [doc#] [--/--/--] [continuation...]
          // Or: [grantor] [grantee] [LAND CONTRACT] [date] [doc#] [--/--/--] [parcel] [address] [related] [amount]
          const grantor = columns.slice(0, lcColIdx >= 2 ? lcColIdx - 1 : 0).join(' ').trim();
          const grantee = lcColIdx >= 2 ? columns[lcColIdx - 1]?.trim() : '';
          const grantorFinal = grantor || (lcColIdx >= 1 ? columns[0]?.trim() : '');
          const granteeFinal = lcColIdx >= 2 ? columns[lcColIdx - 1]?.trim() : (lcColIdx >= 1 ? '' : '');
          
          const dateStr = columns[lcColIdx + 1]?.trim() || '';
          const docNum = columns[lcColIdx + 2]?.trim() || '';
          
          // Find amount in remaining columns
          const remaining = columns.slice(lcColIdx + 3).join('\t');
          const amountMatch = remaining.match(/\$[\d,.]+/g);
          const amount = amountMatch?.[amountMatch.length - 1] || '';
          
          // Find property address in remaining (look for Ohio address pattern)
          const addrMatch = remaining.match(/\d+\s+[\w\s]+(?:AVE|ST|RD|DR|BLVD|CT|LN|TR|WAY|CIR|PL|PKWY),\s*[\w\s]+,\s*OHIO,\s*\d{5}/i);
          const propertyAddr = addrMatch?.[0] || '';
          
          if (lcColIdx === 0) {
            // LAND CONTRACT is the first column - names come after or were in previous line
            columns = [docNum, 'LAND CONTRACT', dateStr, grantorFinal, granteeFinal, propertyAddr, amount];
          } else if (lcColIdx === 1) {
            // [grantor] [LAND CONTRACT] [date] [doc#] ...
            columns = [docNum, 'LAND CONTRACT', dateStr, columns[0]?.trim(), '', propertyAddr, amount];
          } else {
            // [grantor] [grantee] [LAND CONTRACT] [date] [doc#] ...
            columns = [docNum, 'LAND CONTRACT', dateStr, columns[0]?.trim(), columns[lcColIdx - 1]?.trim() || columns[1]?.trim(), propertyAddr, amount];
          }
        }
      }

      // If not tab-separated or tab parsing didn't find LAND CONTRACT, try run-on text
      if (columns.length < 4 || (columns[1] !== 'LAND CONTRACT')) {
        const upper = line.toUpperCase();

        // Variant A (some counties): DOC# then LAND CONTRACT...
        const docFirstMatch = line.match(/^(\d+)(LAND CONTRACTS?)\s*(.+?)(\d{1,2}\/\d{1,2}\/\d{4})(\d+)(.+?)(\$[\d,.]+)?$/i);
        if (docFirstMatch) {
          const [, docNum, docType, namesPart, date, _instrumentNum, legal, amount] = docFirstMatch;

          const namesUpper = namesPart.trim();
          const midPoint = Math.floor(namesUpper.length / 2);
          const grantorName = namesUpper.substring(0, midPoint).trim();
          const granteeName = namesUpper.substring(midPoint).trim();

          columns = [docNum, docType, date, grantorName, granteeName, legal, amount || '$0.00'];
        } else {
          // Variant B (Cuyahoga): NAMES... LAND CONTRACT MM/DD/YYYY + 12-digit doc# ... $amount
          const docTypeIdx = upper.indexOf('LAND CONTRACT');
          if (docTypeIdx > 0) {
            const namesPart = line.slice(0, docTypeIdx).trim();
            const afterDocType = line.slice(docTypeIdx);

            const dateMatch = afterDocType.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
            const amountMatch = afterDocType.match(/\$[\d,.]+/g);

            // Extract doc number: find 12 digits right after the date (they run together)
            let documentNumber = '';
            if (dateMatch) {
              const dateEndIdx = afterDocType.indexOf(dateMatch[1]) + dateMatch[1].length;
              const afterDate = afterDocType.slice(dateEndIdx);
              const docNumAfterDate = afterDate.match(/^(\d{12})/);
              if (docNumAfterDate) {
                documentNumber = docNumAfterDate[1];
              }
            }
            // Fallback: try standalone 12-digit number
            if (!documentNumber) {
              const standaloneMatch = afterDocType.match(/\b(\d{12})\b/);
              if (standaloneMatch) documentNumber = standaloneMatch[1];
            }

            // Normalize vendor/vendee/seller/buyer markers to split grantor/grantee
            const normalizedNames = namesPart
              .replace(/\(\s*VEND(?:OR|ER)\s*\)/gi, ' |VENDOR| ')
              .replace(/\(\s*VEND(?:EE|E)\s*\)/gi, ' |VENDEE| ')
              .replace(/\(\s*SELLER\s*\)/gi, ' |VENDOR| ')
              .replace(/\(\s*BUYER\s*\)/gi, ' |VENDEE| ')
              .replace(/\(\s*ASSIGNOR\s*\)/gi, ' |VENDOR| ')
              .replace(/\(\s*ASSIGNEE\s*\)/gi, ' |VENDEE| ')
              .replace(/[\-:]\s*VEND(?:OR|ER)\b/gi, ' |VENDOR| ')
              .replace(/[\-:]\s*VEND(?:EE|E)\b/gi, ' |VENDEE| ')
              .replace(/[\-:]\s*SELLER\b/gi, ' |VENDOR| ')
              .replace(/[\-:]\s*BUYER\b/gi, ' |VENDEE| ')
              .replace(/\bVEND(?:OR|ER)\b/gi, ' |VENDOR| ')
              .replace(/\bVEND(?:EE|E)\b/gi, ' |VENDEE| ')
              .replace(/\bSELLER\b/gi, ' |VENDOR| ')
              .replace(/\bBUYER\b/gi, ' |VENDEE| ')
              .replace(/\bASSIGNOR\b/gi, ' |VENDOR| ')
              .replace(/\bASSIGNEE\b/gi, ' |VENDEE| ')
              .replace(/\s+/g, ' ')
              .trim();

            let grantorName = normalizedNames;
            let granteeName = '';

            if (normalizedNames.includes('|VENDOR|')) {
              const [beforeVendor, afterVendor] = normalizedNames.split('|VENDOR|').map(s => s.trim());
              grantorName = beforeVendor;
              if (afterVendor?.includes('|VENDEE|')) {
                granteeName = afterVendor.split('|VENDEE|')[0].trim();
              } else {
                granteeName = afterVendor?.trim() || '';
              }
            } else if (normalizedNames.includes('|VENDEE|')) {
              const [beforeVendee, afterVendee] = normalizedNames.split('|VENDEE|').map(s => s.trim());
              grantorName = beforeVendee;
              granteeName = afterVendee || '';
            } else {
              // Fallback: split names roughly in half
              const midPoint = Math.floor(normalizedNames.length / 2);
              grantorName = normalizedNames.substring(0, midPoint).trim();
              granteeName = normalizedNames.substring(midPoint).trim();
            }

            // Clean up any remaining pipe markers from names
            grantorName = grantorName.replace(/\|VENDOR\||\|VENDEE\|/g, '').trim();
            granteeName = granteeName.replace(/\|VENDOR\||\|VENDEE\|/g, '').trim();

            const filingDateStr = dateMatch?.[1] || '';
            const amountStr = amountMatch?.[amountMatch.length - 1] || '$0.00';

            if (documentNumber) {
              columns = [documentNumber, 'LAND CONTRACT', filingDateStr, grantorName, granteeName, '', amountStr];
            }
          }

          // Last resort: try splitting by multiple spaces
          if (columns.length < 4) {
            columns = line.split(/\s{2,}/);
          }
        }
      }
      
      if (columns.length < 4) continue;
      
      stats.total++;

      // Extract fields
      const documentNumber = columns[0]?.trim() || '';
      const documentType = columns[1]?.trim() || '';
      const filingDateStr = columns[2]?.trim() || '';
      const grantorName = columns[3]?.trim() || '';
      const granteeName = columns[4]?.trim() || '';
      const legalDesc = columns[5]?.trim() || '';
      const amountStr = columns[6]?.trim() || '';

      // Only process land contracts
      const docTypeUpper = documentType.toUpperCase();
      if (!docTypeUpper.includes('LAND') && !docTypeUpper.includes('CONTRACT') && docTypeUpper !== 'LC') {
        continue;
      }
      stats.landContracts++;

      // Check for family transfer
      if (isFamilyTransfer(grantorName, granteeName)) {
        stats.familyTransfers++;
        continue;
      }

      stats.passed++;

      // Parse filing date
      let filingDate = new Date().toISOString().split('T')[0];
      const dateMatch = filingDateStr.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
      if (dateMatch) {
        const month = dateMatch[1].padStart(2, '0');
        const day = dateMatch[2].padStart(2, '0');
        let year = dateMatch[3];
        if (year.length === 2) {
          year = parseInt(year) > 50 ? '19' + year : '20' + year;
        }
        filingDate = `${year}-${month}-${day}`;
      }

      // Parse amount
      let loanAmount: number | null = null;
      if (amountStr) {
        const cleaned = amountStr.replace(/[$,]/g, '');
        const parsed = parseFloat(cleaned);
        if (!isNaN(parsed)) loanAmount = parsed;
      }

      // Extract property address from legal description or remaining data
      const propertyAddress = legalDesc && /\d+\s+[\w\s]+,/.test(legalDesc) ? legalDesc : null;

      records.push({
        county: 'cuyahoga',
        document_number: documentNumber,
        document_type: 'LAND CONTRACT',
        filing_date: filingDate,
        grantor_name: grantorName,
        grantee_name: granteeName,
        legal_description: legalDesc || null,
        loan_amount: loanAmount,
        property_address: propertyAddress,
        is_potential_owner_finance: true,
        confidence_score: 95,
        address_extraction_method: 'text_import',
        status: 'new',
        pdf_downloaded: false,
        pdf_parsed: false,
        ocr_attempted: false,
        address_needs_review: true,
        grantor_address: null,
        grantee_address: null
      });
    }

    return { records, stats };
  };

  const processText = async (text: string) => {
    if (!text.trim()) {
      toast.error('Please paste the record data first');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);
    setFilterStats(null);

    try {
      const { records, stats } = parseRecords(text);
      setFilterStats(stats);

      if (records.length === 0) {
        toast.error('No valid records found after filtering');
        setIsProcessing(false);
        return;
      }

      console.log('Cuyahoga County Import Stats:', stats);

      const result: ImportResult = {
        success: 0,
        duplicates: 0,
        familyTransfers: stats.familyTransfers,
        failed: 0,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          // Check for duplicate
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('county', 'cuyahoga')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            result.duplicates++;
            continue;
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            result.failed++;
            result.failedRecords.push({
              docNumber: record.document_number,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (err) {
          result.failed++;
          result.failedRecords.push({
            docNumber: record.document_number,
            error: String(err)
          });
        }
      }

      setImportResult(result);

      if (result.success > 0) {
        toast.success(`Imported ${result.success} records`);
        setPastedText('');
        onRecordsSaved?.();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handlePasteImport = () => {
    processText(pastedText);
  };

  // Screenshot capture handlers
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            setImageData(base64);
            setExtractedData(null);
            toast.success('Screenshot captured! Click "Extract Data" to process.');
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }
    toast.error('No image found in clipboard');
  }, []);

  const handleExtract = async () => {
    if (!imageData) {
      toast.error('No screenshot to process');
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('extract-contract-screenshot', {
        body: { imageData, county: 'cuyahoga' }
      });

      if (error) throw error;

      if (data?.extractedData) {
        setExtractedData(data.extractedData);
        toast.success('Data extracted! Review and save.');
      } else {
        toast.error('No data could be extracted');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error(`Extraction failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData?.document_number) {
      toast.error('Document number is required');
      return;
    }

    setIsProcessing(true);
    try {
      const { data: existing } = await supabase
        .from('county_research')
        .select('id')
        .eq('county', 'cuyahoga')
        .eq('document_number', extractedData.document_number)
        .maybeSingle();

      if (existing) {
        toast.warning(`Document ${extractedData.document_number} already exists`);
        handleClear();
        setSkippedCount(prev => prev + 1);
        return;
      }

      const record = {
        county: 'cuyahoga',
        document_number: extractedData.document_number,
        document_type: extractedData.document_type || 'LAND CONTRACT',
        filing_date: extractedData.filing_date || new Date().toISOString().split('T')[0],
        grantor_name: extractedData.grantor_name,
        grantor_address: extractedData.grantor_address,
        grantee_name: extractedData.grantee_name,
        grantee_address: extractedData.grantee_address,
        property_address: extractedData.property_address,
        legal_description: extractedData.legal_description,
        loan_amount: extractedData.loan_amount,
        interest_rate: extractedData.interest_rate,
        monthly_payment: extractedData.monthly_payment,
        term_months: extractedData.term_months,
        balloon_amount: extractedData.balloon_amount,
        balloon_due_date: extractedData.balloon_due_date,
        is_potential_owner_finance: true,
        confidence_score: 90,
        address_extraction_method: 'screenshot_ai',
        status: 'new',
        address_needs_review: !extractedData.grantor_address
      };

      const { error } = await supabase.from('county_research').insert(record);
      if (error) throw error;

      toast.success(`Saved: ${extractedData.document_number}`);
      setSavedCount(prev => prev + 1);
      handleClear();
      onRecordsSaved?.();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkip = () => {
    setSkippedCount(prev => prev + 1);
    handleClear();
    toast.info('Record skipped');
  };

  const handleClear = () => {
    setImageData(null);
    setExtractedData(null);
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="import" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="import">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </TabsTrigger>
          <TabsTrigger value="screenshot">
            <Camera className="w-4 h-4 mr-2" />
            Screenshot Capture
          </TabsTrigger>
        </TabsList>

        {/* IMPORT TAB */}
        <TabsContent value="import" className="space-y-4">
          {filterStats && (
            <Alert>
              <Filter className="h-4 w-4" />
              <AlertDescription>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>Total Lines: <strong>{filterStats.total}</strong></div>
                  <div>Land Contracts: <strong>{filterStats.landContracts}</strong></div>
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Family Transfers: <strong className="text-amber-600">{filterStats.familyTransfers}</strong>
                  </div>
                  <div>Passed: <strong className="text-emerald-600">{filterStats.passed}</strong></div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {importResult && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <div><strong>Import Complete:</strong></div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div>✓ Imported: <strong className="text-emerald-600">{importResult.success}</strong></div>
                    <div>⊘ Duplicates: <strong>{importResult.duplicates}</strong></div>
                    <div>👥 Family: <strong className="text-amber-600">{importResult.familyTransfers}</strong></div>
                  </div>
                  {importResult.failed > 0 && (
                    <div className="text-destructive text-sm">
                      ✗ Failed: {importResult.failed}
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Cuyahoga County Data Import</CardTitle>
              <CardDescription>
                Paste tab-separated data from Cuyahoga County. Auto-filters: duplicates, family-to-family transfers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste Cuyahoga County land contract data here...

Example format (tab-separated):
Doc#    Type    Date    Grantor    Grantee    Legal    Amount
202401234    LAND CONTRACT    01/15/2024    SMITH JOHN    DOE JANE    LOT 5 BLK 2    125000"
                className="min-h-[300px] font-mono text-sm"
                disabled={isProcessing}
              />

              <Button
                onClick={handlePasteImport}
                disabled={isProcessing || !pastedText.trim()}
                className="w-full"
              >
                {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Upload className="mr-2 h-4 w-4" />
                Import & Filter Data
              </Button>

              {isProcessing && (
                <div className="space-y-2">
                  <Progress value={progress} />
                  <p className="text-sm text-center text-muted-foreground">
                    Processing... {progress}%
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Auto-Filtering Rules:</strong>
              <ul className="list-disc ml-4 mt-1 text-sm space-y-1">
                <li><strong>Duplicates:</strong> Skips records already in database</li>
                <li><strong>Family Transfers:</strong> Same last name for grantor/grantee</li>
                <li><strong>Business Sellers:</strong> LLC, Inc, Corp, Trust, Bank, etc.</li>
              </ul>
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* SCREENSHOT CAPTURE TAB */}
        <TabsContent value="screenshot" className="space-y-4">
          <div className="flex gap-4 items-center">
            <Badge variant="outline" className="text-lg px-4 py-2">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Saved: {savedCount}
            </Badge>
            <Badge variant="outline" className="text-lg px-4 py-2">
              <SkipForward className="w-4 h-4 mr-2 text-yellow-500" />
              Skipped: {skippedCount}
            </Badge>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Full Record Screenshot Capture
              </CardTitle>
              <CardDescription>
                <strong>Workflow:</strong> Screenshot the full Cuyahoga record page (both contract + summary panel) → Extract ALL data → Save complete record
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  imageData ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-muted-foreground/25 hover:border-primary'
                }`}
                onPaste={handlePaste}
                tabIndex={0}
                onClick={(e) => (e.target as HTMLElement).focus()}
              >
                {imageData ? (
                  <div className="space-y-4">
                    <p className="text-green-600 dark:text-green-400 font-medium">✓ Screenshot captured!</p>
                    <img 
                      src={imageData} 
                      alt="Captured screenshot" 
                      className="max-h-[400px] mx-auto border rounded shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
                    <p className="text-lg font-medium">Click here, then Ctrl+V to paste full page screenshot</p>
                    <p className="text-sm text-muted-foreground">
                      Capture the ENTIRE record page — both the contract document (left) AND the summary panel (right)
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleExtract}
                  disabled={!imageData || isProcessing}
                  className="flex-1"
                >
                  {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Extract Data
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={!imageData || isProcessing}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              </div>

              {extractedData && (
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Extracted Data — Full Record</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Document #:</span>
                        <p className="font-mono">{extractedData.document_number || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Filing Date:</span>
                        <p>{extractedData.filing_date || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium text-muted-foreground">Seller (Grantor):</span>
                        <p className="font-semibold">{extractedData.grantor_name || 'N/A'}</p>
                      </div>
                      <div className="col-span-2 p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
                        <span className="font-medium text-muted-foreground">⭐ Seller Address:</span>
                        <p className={`font-semibold text-lg ${extractedData.grantor_address ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                          {extractedData.grantor_address || '⚠️ Not found - may need to scroll contract'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium text-muted-foreground">Buyer (Grantee):</span>
                        <p>{extractedData.grantee_name || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium text-muted-foreground">Property Address:</span>
                        <p>{extractedData.property_address || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Legal Description:</span>
                        <p className="text-xs">{extractedData.legal_description || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Loan Amount:</span>
                        <p className="font-semibold">{extractedData.loan_amount ? `$${extractedData.loan_amount.toLocaleString()}` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Interest Rate:</span>
                        <p>{extractedData.interest_rate ? `${extractedData.interest_rate}%` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Monthly Payment (Base):</span>
                        <p>{extractedData.monthly_payment ? `$${extractedData.monthly_payment.toLocaleString()}` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Term:</span>
                        <p>{extractedData.term_months ? `${extractedData.term_months} months` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Balloon:</span>
                        <p>{extractedData.balloon_amount ? `$${extractedData.balloon_amount.toLocaleString()}` : 'None'}</p>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button onClick={handleSave} disabled={isProcessing} className="flex-1">
                        {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        <Save className="mr-2 h-4 w-4" />
                        Save & Next
                      </Button>
                      <Button variant="outline" onClick={handleSkip} disabled={isProcessing}>
                        <SkipForward className="mr-2 h-4 w-4" />
                        Skip
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
